import React, { forwardRef, useImperativeHandle, useEffect, useState, useRef, useMemo } from 'react';
import { Spin } from 'antd';
import s from './s.module.less';
import pageS from '../../s.module.less';
import PracticeForm from './components/PracticeForm';
import PracticeAddress from './components/PracticeAddress';
import { usePracticeProfile } from 'hooks/paymentSetup/usePracticeProfile';
import { editPracticeProfile } from 'api/paymentSetup';
import { Address, ApiPracticeProfile, PracticeInfo } from 'types/paymentSetup';

interface PracticeProfileStepProps {
    isEdit?: boolean;
    isReview?: boolean;
    showPracticeForm?: boolean;
    showPracticeAddress?: boolean;
    type?: 'onboarding' | 'payment';
}

const defaultPracticeInfo = { practiceName: '', practiceEmail: '' };
const defaultAddressList = [{
    practiceAddress: '',
    practiceCity: '',
    practiceState: '',
    practiceZip: '',
    publicType: undefined,
    isDefault: false,
    asMailing: false,
}];

const PracticeProfileStep = forwardRef<any, PracticeProfileStepProps>(({
    isEdit = true,
    isReview = false,
    showPracticeAddress = true,
    showPracticeForm = true,
    type = 'payment',
}, ref) => {
    const [apiFormData, loading, fetchData] = usePracticeProfile();
    const practiceRef = useRef<any>(null);
    const addressRef = useRef<any>(null);

    const [practiceInfo, setPracticeInfo] = useState<PracticeInfo>(defaultPracticeInfo);
    const [addressList, setAddressList] = useState<Address[]>(defaultAddressList);

    const defaultPracticeName = useMemo(() => {
        return `${apiFormData.firstName} ${apiFormData.lastName}'s Practice`;
    }, [apiFormData]);

    useEffect(() => {
        if (apiFormData) {
            setPracticeInfo({
                practiceName: apiFormData.practiceName || defaultPracticeName,
                practiceEmail: apiFormData.practiceEmail || '',
                practicePhone: apiFormData.practicePhone,
                practiceDesc: apiFormData.practiceDesc,
                practiceLogo: apiFormData.practiceLogo,
                taxId: apiFormData.taxId,
                taxIdType: apiFormData.taxIdType,
                notHavePhone: apiFormData.notHavePhone || false,
            });
            setAddressList(apiFormData.addressList || []);
        }
    }, [apiFormData]);

    const handleStepSubmit = async () => {
        try {
            const practiceFormValues = await practiceRef.current?.getFormValues();
            const addressFormValues = await addressRef.current?.getFormValues();
            // 在 review 里单独修改 practiceFormValues 时，addressFormValues 会为空，但是接口需要带上
            // 单独修改 addressFormValues 时同理
            const apiPayload: ApiPracticeProfile = {
                step: 'PRACTICE',
                providerPractice: {
                    ...(practiceFormValues || practiceInfo),
                    addressList: addressFormValues?.addressList.length > 0 ? addressFormValues.addressList : addressList,
                },
            };
            await editPracticeProfile(apiPayload);
            await fetchData();
        } catch (errorInfo) {
            console.error('Practice Profile validation/save failed:', errorInfo);
            throw errorInfo;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleStepSubmit,
    }));

    return (
        <Spin spinning={loading}>
            <div className={`${s.practiceProfileStep} ${type === 'onboarding' ? s.onboardingPracticeProfileStep : ''}`}>
                {!isReview && (
                    <div className={s.header}>
                        <h2 className={pageS.stepTitle}>Practice profile</h2>
                        <p className={pageS.desc}>Share your practice details to help patients recognize, trust, and connect with you.</p>
                    </div>
                )}

                <div className={s.formContainer}>
                    {showPracticeForm ?
                        <div className={s.formSection}>
                            {!isReview && (
                                <>
                                    <h3 className={pageS.title}>Practice information</h3>
                                    <p className={pageS.desc}>Provide your practice name, contact info, and other essential details.</p>
                                </>
                            )}
                            <PracticeForm
                                ref={practiceRef}
                                initFormValue={practiceInfo}
                                initPracticeName={defaultPracticeName}
                                enable={isEdit}
                                providerId={apiFormData.id}
                                conversationId={apiFormData.practiceDescConversationId}
                            />

                        </div> : null}

                    {showPracticeAddress ?
                        <div className={s.formSection}>
                            {!isReview && (
                                <>
                                    <h3 className={pageS.title}>Practice address</h3>
                                    <p className={pageS.desc}>Add your primary location, or indicate where your services are based.</p>
                                </>
                            )}
                            <PracticeAddress
                                ref={addressRef}
                                formName="addressForm"
                                initFormValue={addressList}
                                enable={isEdit}
                            />
                        </div> : null}
                </div>
            </div>
        </Spin>
    );
});

export default PracticeProfileStep;
